import type { ComponentProps } from "react";

import { cn } from "../../lib/utils";
import { useSelectContext } from "./SelectContext";
import { SelectTriggerButton } from "./SelectTriggerButton";
import { SelectBadgeList } from "./SelectBadgeList";
import { SelectTriggerClearableRow } from "./SelectTriggerClearableRow";

export type SelectTriggerProps = ComponentProps<"button"> & {
  /**
   * `inline` is chrome-free: no border, shadow, or fill (light or dark), and no hover fill — for a
   * trigger inside a toolbar or header that already provides the surface. Focus styling is kept.
   * `ghost` is transparent at rest like `inline` but paints a hover fill, matching a ghost `Button`.
   * @default "default"
   */
  variant?: "default" | "inline" | "ghost";
  /**
   * `auto` sizes the trigger to its selected value, which renders in full instead of truncating.
   * `default` stretches the value to the trigger's width and truncates it with an ellipsis.
   * @default "default"
   */
  width?: "default" | "auto";
  /** Open the dropdown whenever the trigger receives focus (e.g. tabbing through a form). */
  openOnFocus?: boolean;
  /**
   * Use the single child element (e.g. a design-system `Button`) as the trigger instead of the
   * default field-styled one. It receives the trigger's ARIA and keyboard wiring, keeps its own
   * content and button role, and is named by its own text. In multi-select, the badge list isn't
   * rendered — a custom trigger shows the selection its own way.
   */
  asChild?: boolean;
  /**
   * In multi-select, leave out the badge list under the field — for tight spaces like a toolbar. The
   * field and its clear button stay; `Select.Value`'s count (or its children) summarizes the selection.
   */
  hideBadges?: boolean;
};

/**
 * The field that opens the dropdown. On a `clearable` Select it also renders the clear button beside
 * the field, and in multi-select the selected values as badges underneath; `className` then sizes the
 * field together with its clear button and badges, and the badges wrap within that width.
 */
export const SelectTrigger = ({ asChild = false, hideBadges = false, className, ...props }: SelectTriggerProps) => {
  const { multiple, clearable } = useSelectContext();
  const showBadges = multiple && !hideBadges;
  if (asChild || (!showBadges && !clearable)) {
    return <SelectTriggerButton asChild={asChild} className={className} {...props} />;
  }
  if (!showBadges) return <SelectTriggerClearableRow className={className} {...props} />;

  return (
    <div data-slot="select-trigger-group" className={cn("flex min-w-0 flex-col gap-1.5", className)}>
      {clearable ? <SelectTriggerClearableRow {...props} /> : <SelectTriggerButton className="w-full" {...props} />}
      <SelectBadgeList />
    </div>
  );
};
